const categories = require('./constants/categories.json');
const { hogesErrCodes, stmErrCodes, sharedErrCodes } = require('./constants/errCodes');
const {
  hogesProctoringEvents,
  hogesTestStatusEvents,
  hogesFraudEvents,
  hogesProgressEvents,
} = require('./constants/events');
const { hePlatforms } = require('./constants/infra');

/**
 * Enum for ENVIRONMENTS values.
 *
 * @readonly
 * @enum {string}
 */
const ENVIRONMENTS = {
  DEVELOPMENT: 'development',
  LOCAL: 'local',
  PRODUCTION: 'production',
  STAGING: 'staging',
  TEST: 'test',
};

/**
 * @type {number}
 */
const COURSE_FEES = 8;

/**
 * Enum for OAUTH values.
 *
 * @readonly
 * @enum {string}
 */
const OAUTH = {
  CANVAS: 'canvas',
  GOOGLE: 'google',
};

/**
 * Enum for ROLES values.
 *
 * @readonly
 * @enum {string}
 */
const ROLES = {
  ADMIN: 'admin',
  STUDENT: 'student',
  TEACHER: 'teacher',
  TEST: 'test',
  EDITOR: 'editor',
};

/**
 * Enum for FILE_EXTENSION values.
 *
 * @readonly
 * @enum {string}
 */
const FILE_EXTENSION = {
  'application/msword': '.doc',
  'application/pdf': '.pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template': '.dotx',
  'application/vnd.ms-word.document.macroEnabled.12': '.docm',
  'application/vnd.ms-word.template.macroEnabled.12': '.dotm',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template': '.xltx',
  'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
  'application/vnd.ms-excel.template.macroEnabled.12': '.xltm',
  'application/vnd.ms-excel.addin.macroEnabled.12': '.xlam',
  'application/vnd.ms-excel.sheet.binary.macroEnabled.12': '.xlsb',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/vnd.openxmlformats-officedocument.presentationml.template': '.potx',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow': '.ppsx',
  'application/vnd.ms-powerpoint.addin.macroEnabled.12': '.ppam',
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12': '.pptm',
  'application/vnd.ms-powerpoint.template.macroEnabled.12': '.potm',
  'application/vnd.ms-powerpoint.slideshow.macroEnabled.12': '.ppsm',
  'application/vnd.ms-access': '.mdb',
};

/**
 * Enum for REVIEWABLE STATUS values.
 *
 * @readonly
 * @enum {string}
 */
const REVIEWABLE_STATUS = {
  FAILED: 'failed',
  PASSED: 'passed',
  PENDING_REVIEW: 'pending',
};

/**
 * List of book categories
 *
 * @readonly
 */
const BOOK_CATEGORIES = categories;

/**
 * List of custom errors
 *
 * @readonly
 */
const HE_ERROR_CODES = sharedErrCodes;
const STM_ERROR_CODES = stmErrCodes;
const HST_ERROR_CODES = hogesErrCodes;

/**
 * List of test environment events
 *
 * @readonly
 */
const HST_PROCTORING_EVENTS = hogesProctoringEvents;
const HST_TEST_STATUS_EVENTS = hogesTestStatusEvents;
const HST_FRAUD_EVENTS = hogesFraudEvents;
const HST_PROGRESS_EVENTS = hogesProgressEvents;

/**
 * List of HE platforms
 *
 * @readonly
 */
const HE_PLATFORMS = hePlatforms;

/**
 * Enum for PRICING MODEL values.
 *
 * @readonly
 * @enum {string}
 */
const PRICING_MODEL = {
  ORIGINAL: 'original',
  DIGITAL_ONLY: 'digitalOnly',
  DIGITAL_PRINT: 'digitalPrint',
  PRINT_ONLY: 'printOnly',
  LATE_POD_FEE: 10,
  NOT_ALL_CHAPTERS_FEE: 5,
};

/**
 * Enum for BUNDLE OPTON values.
 *
 * @readonly
 * @enum {string}
 */
const BUNDLE_OPTIONS = {
  DIGITAL_ONLY: 'digitalOnly',
  DIGITAL_PRINT: 'digitalPrint',
  PRINT_ONLY: 'printOnly',
};

/**
 * Method to validate database connection in test environment
 *
 * @param {string} uri your database url
 *
 * @param {string[]} invalidDBCharacters array of invalid url connections
 *
 * @param {string} environmentKey environment key you want to validate
 *
 */
const validateURL = (uri, invalidDBCharacters, environmentKey) => {
  invalidDBCharacters.forEach(invalidUri => {
    if (uri.includes(invalidUri) && process.env[environmentKey] === ENVIRONMENTS.TEST) {
      throw new Error('Be careful you are not testing with test DB URL. If you proceed there\'s a big chance of deleting the files of a data base and your job');
    }
  });
};

// eslint-disable-next-line jsdoc/require-returns
/**
 * Validate if the X-Api-Key is valid
 *
 * @param {string} xApiKey initialize X-Api-Key to compare
 *
 */
const authXApiKey = xApiKey => (request, response, next) => {
  if (request.headers['x-api-key'] !== xApiKey) {
    response.sendStatus(401);
  } else {
    next();
  }
};

/**
 * Enum for BREADCRUMBS values.
 *
 * @readonly
 * @enum {string}
 */
const BREADCRUMBS = {
  SUMMARY: 'SUMMARY',
  INTRO_TEXT: 'INTROTEXT',
  THEORY: 'THEORY',
  ASSIGNMENT: 'ASSIGNMENT',
  CORE: 'CORE',
  TEST: 'TEST',
  PACKAGE: 'PACKAGE',
  THEME: 'THEME',
  MATERIAL_ASSETS: 'MATERIALASSETS',
  EXTRA_ASSETS: 'EXTRAASSETS',
  DRILLING: 'DRILLING',
};

/**
 * Enum for content model values.
 *
 * @readonly
 * @enum {string}
 */
const CONTENT_MODEL = {
  NEW: 'new',
  MIGRATED: 'migrated',
  MIXED_MIGRATED: 'mixed_migrated',
  NO_MIGRATED: 'no_migrated',
  WILL_BE_MIGRATED: 'will_be_migrated',
};

/**
 * Enum for ecommerce item type values.
 *
 * @readonly
 * @enum {string}
 */
const ECOMMERCE_ITEM_TYPES = {
  COURSE: 'course',
  PRINT: 'print',
  SHIPPING: 'shipping',
};

/**
 * Enum for course types.
 *
 * @readonly
 * @enum {string}
 */
const COURSE_TYPES = {
  BLENDED: 'blended',
  WEEK: 'week',
  ONLINE_BOOK: 'online-book',
};

// eslint-disable-next-line
module.exports = {
  BOOK_CATEGORIES,
  ENVIRONMENTS,
  OAUTH,
  ROLES,
  FILE_EXTENSION,
  REVIEWABLE_STATUS,
  COURSE_FEES,
  HE_ERROR_CODES,
  STM_ERROR_CODES,
  HST_ERROR_CODES,
  HST_PROCTORING_EVENTS,
  HE_PLATFORMS,
  HST_TEST_STATUS_EVENTS,
  HST_FRAUD_EVENTS,
  HST_PROGRESS_EVENTS,
  validateURL,
  authXApiKey,
  PRICING_MODEL,
  BUNDLE_OPTIONS,
  BREADCRUMBS,
  CONTENT_MODEL,
  ECOMMERCE_ITEM_TYPES,
  COURSE_TYPES,
};
